/**
 * 自動明度・コントラスト補正 (ヒストグラム引き伸ばし)。
 *
 * SPEC §2.2 の CLAHE は OpenCV 依存で Web 移植コストが高いため、
 * 本実装は簡易版 (per-channel の 1%-99% 引き伸ばし)。
 * 商品画像としての品質が不十分であれば Wave 2 後半で wasm OpenCV 等へ切替検討。
 *
 * - `calculateStretchRange` は純粋関数
 * - `autoEnhance` は Canvas 依存 (ブラウザ / Worker で実行)
 */

import { type ImageDataLike } from './types-canvas';

const CHANNELS = 3 as const; // R, G, B の 3 チャンネル (Alpha は触らない)
const HISTOGRAM_BINS = 256 as const;
const DEFAULT_LOW_PERCENT = 0.01; // 1% の暗部を切り詰め
const DEFAULT_HIGH_PERCENT = 0.99; // 99% の明部を切り詰め

/**
 * ヒストグラムから percentile 範囲を計算する (純粋関数)。
 */
export function calculateStretchRange(
  histogram: readonly number[],
  totalPixels: number,
  lowPercent: number = DEFAULT_LOW_PERCENT,
  highPercent: number = DEFAULT_HIGH_PERCENT,
): { min: number; max: number } {
  assertHistogramInputs(histogram, totalPixels, lowPercent, highPercent);
  const { min, max } = scanPercentiles(histogram, totalPixels, lowPercent, highPercent);
  if (max <= min) {
    return { min: 0, max: HISTOGRAM_BINS - 1 };
  }
  return { min, max };
}

function assertHistogramInputs(
  histogram: readonly number[],
  totalPixels: number,
  lowPercent: number,
  highPercent: number,
): void {
  if (histogram.length !== HISTOGRAM_BINS) {
    throw new RangeError(`histogram length must be ${HISTOGRAM_BINS}`);
  }
  if (totalPixels <= 0) {
    throw new RangeError('totalPixels must be positive');
  }
  if (lowPercent < 0 || lowPercent >= highPercent || highPercent > 1) {
    throw new RangeError(`invalid percentiles: low=${lowPercent} high=${highPercent}`);
  }
}

function scanPercentiles(
  histogram: readonly number[],
  totalPixels: number,
  lowPercent: number,
  highPercent: number,
): { min: number; max: number } {
  const lowThreshold = totalPixels * lowPercent;
  const highThreshold = totalPixels * highPercent;
  let acc = 0;
  let min = 0;
  let max = HISTOGRAM_BINS - 1;
  let foundMin = false;
  for (let i = 0; i < HISTOGRAM_BINS; i++) {
    acc += histogram[i] ?? 0;
    if (!foundMin && acc >= lowThreshold) {
      min = i;
      foundMin = true;
    }
    if (acc >= highThreshold) {
      max = i;
      break;
    }
  }
  return { min, max };
}

/**
 * 1 チャンネル分のヒストグラムを ImageData のチャンネル index から計算 (純粋関数)。
 *
 * @param pixels - RGBA の連続配列 (長さ 4N)
 * @param channel - 0=R, 1=G, 2=B
 */
export function computeChannelHistogram(
  pixels: Uint8ClampedArray | readonly number[],
  channel: 0 | 1 | 2,
): number[] {
  const hist = new Array<number>(HISTOGRAM_BINS).fill(0);
  for (let i = channel; i < pixels.length; i += 4) {
    const value = pixels[i] ?? 0;
    hist[value] = (hist[value] ?? 0) + 1;
  }
  return hist;
}

/**
 * pixel 配列に対して per-channel の線形引き伸ばしを適用する (純粋関数)。
 * **破壊的**: 第一引数の配列を直接書き換える (性能と省メモリのため)。
 */
export function applyStretchInPlace(
  pixels: Uint8ClampedArray,
  ranges: readonly { min: number; max: number }[],
): void {
  if (ranges.length !== CHANNELS) {
    throw new RangeError(`ranges length must be ${CHANNELS}`);
  }
  for (let i = 0; i < pixels.length; i += 4) {
    for (let c = 0; c < CHANNELS; c++) {
      const range = ranges[c];
      if (!range) continue;
      const v = pixels[i + c] ?? 0;
      const stretched = ((v - range.min) * 255) / (range.max - range.min);
      pixels[i + c] = clampUint8(stretched);
    }
  }
}

/**
 * ImageData に対して自動補正を実施する (純粋ロジック、Canvas 不要)。
 * 新しい Uint8ClampedArray を返す (入力は不変)。
 */
export function enhanceImageData(input: ImageDataLike): Uint8ClampedArray {
  const pixels = new Uint8ClampedArray(input.data);
  const totalPixels = input.width * input.height;
  const ranges = [0, 1, 2].map((ch) => {
    const hist = computeChannelHistogram(pixels, ch as 0 | 1 | 2);
    return calculateStretchRange(hist, totalPixels);
  });
  applyStretchInPlace(pixels, ranges);
  return pixels;
}

/**
 * ImageBitmap に自動補正を適用して新しい ImageBitmap を返す (Canvas 依存)。
 */
export async function autoEnhance(source: ImageBitmap): Promise<ImageBitmap> {
  const canvas = new OffscreenCanvas(source.width, source.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('OffscreenCanvas 2D context unavailable');
  }
  ctx.drawImage(source, 0, 0);
  const imageData = ctx.getImageData(0, 0, source.width, source.height);
  const enhanced = enhanceImageData(imageData);
  const outData = ctx.createImageData(source.width, source.height);
  outData.data.set(enhanced);
  ctx.putImageData(outData, 0, 0);
  return canvas.transferToImageBitmap();
}

// ===== 内部ヘルパ =======================================================

function clampUint8(value: number): number {
  if (value < 0) return 0;
  if (value > 255) return 255;
  return Math.round(value);
}
